import { createPortal, memo } from "preact/compat";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import {
  Check,
  DoorOpen,
  Hand,
  Maximize,
  Minus,
  MousePointer2,
  Pentagon,
  Plus,
  Redo2,
  ScanLine,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import type { MapDocument, MapRouting } from "../shared/types";
import { validateMapRouting } from "../shared/validation";
import { VenueMapScene } from "../map/VenueMapScene";
import type { VenueMapLogos } from "../map/useVenueMapLogos";
import {
  barrierPoint,
  clampRoutingPoint,
  constrainRoutingView,
  fitRoutingView,
  moveRoutingVertex,
  projectOntoBarrier,
  removeRoutingConstraint,
  routingSourcePoint,
  routingSelectionVertices,
  routingEditorError,
  type RoutingPoint,
  type RoutingSelection,
  type RoutingView,
} from "./routingEditorGeometry";
import { useRoutingHistory } from "./useRoutingHistory";
import {
  suggestRoutingWalls,
  filterRoutingWallSuggestions,
  snapRoutingPoint,
} from "./routingEditorSuggestions";
import {
  RoutingRoutePreview,
  type RoutingPreviewResult,
} from "./RoutingRoutePreview";
import "./routing-editor.css";

type Tool = "select" | "pan" | "area" | "barrier" | "door";
type Gesture =
  | {
      kind: "tap" | "pan" | "vertex";
      pointerId: number;
      startClient: RoutingPoint;
      startOuter: RoutingPoint;
      startSource: RoutingPoint;
      startView: RoutingView;
      startVertex?: RoutingPoint;
      startRouting?: MapRouting;
      vertex?: number;
      tapSelection?: RoutingSelection | null;
      moved: boolean;
    }
  | { kind: "pinch"; distance: number; zoom: number; anchor: RoutingPoint };

export interface RoutingEditorProps {
  document: MapDocument;
  logos?: VenueMapLogos;
  returnFocusTo?: HTMLElement | null;
  onApply: (routing: MapRouting) => void;
  onClose: () => void;
}

const tools: { id: Tool; label: string; Icon: typeof Hand }[] = [
  { id: "select", label: "Select", Icon: MousePointer2 },
  { id: "pan", label: "Pan", Icon: Hand },
  { id: "area", label: "Walkable area", Icon: Pentagon },
  { id: "barrier", label: "Wall / barrier", Icon: Minus },
  { id: "door", label: "Doorway", Icon: DoorOpen },
];
const emptyRouting = (): MapRouting => ({
  walkableAreas: [],
  barriers: [],
  doors: [],
});
const midpoint = (a: RoutingPoint, b: RoutingPoint): RoutingPoint => [
  (a[0] + b[0]) / 2,
  (a[1] + b[1]) / 2,
];
const pointList = (points: number[][]) =>
  points.map((point) => point.join(",")).join(" ");
const areaPath = (rings: number[][][]) =>
  rings.map((ring) => `M${pointList(ring).replaceAll(" ", "L")}Z`).join(" ");
const percent = (value: number, extent: number) =>
  Number(((value / extent) * 100).toFixed(3));
const newId = (kind: string) => {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return `${kind}-${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
};
const typeName = (kind: RoutingSelection["kind"]) =>
  kind === "area"
    ? "Walkable area"
    : kind === "barrier"
      ? "Wall / barrier"
      : "Doorway";

// Keep the existing map artwork fixed while camera and draft constraints change.
// The editor is SVG-only and does not create another WebGL context.
const RoutingMapScene = memo(function RoutingMapScene({
  document,
  logos,
}: Pick<RoutingEditorProps, "document" | "logos">) {
  const { width, height } = document.geometry;
  return (
    <VenueMapScene
      document={document}
      logos={logos}
      camera={{
        sourceWidth: width,
        sourceHeight: height,
        imageWidth: width,
        imageHeight: height,
        stageWidth: width,
        stageHeight: height,
        rotation: 0,
        pitch: 0,
        perspective: false,
      }}
      viewportBounds={{ x: 0, y: 0, width, height }}
      description="Top-down venue map for routing"
    />
  );
});

export function RoutingEditor({
  document,
  logos,
  returnFocusTo,
  onApply,
  onClose,
}: RoutingEditorProps) {
  const { width, height, floor } = document.geometry;
  const hintId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const closing = useRef(false);
  const history = useRoutingHistory(
    document.geometry.routing ?? emptyRouting(),
  );
  const { routing } = history;
  const routingRef = useRef(routing);
  routingRef.current = routing;
  const [reviewingWalls, setReviewingWalls] = useState(false);
  const [suggestionId, setSuggestionId] = useState<string | null>(null);
  const [dismissedWalls, setDismissedWalls] = useState<Set<string>>(new Set());
  const [snapping, setSnapping] = useState(true);
  const [snapPoint, setSnapPoint] = useState<RoutingPoint | null>(null);
  const [previewRoute, setPreviewRoute] = useState<RoutingPreviewResult | null>(
    null,
  );
  const receivePreviewRoute = useCallback(
    (result: RoutingPreviewResult | null) => setPreviewRoute(result),
    [],
  );
  // Trace once per source document, never during camera or vertex gestures.
  const sourceWalls = useMemo(
    () => suggestRoutingWalls(document.geometry, emptyRouting()),
    [document.geometry],
  );
  const availableWalls = useMemo(
    () =>
      filterRoutingWallSuggestions(
        sourceWalls.walls,
        routing,
        document.geometry,
      ),
    [sourceWalls, routing.barriers, document.geometry],
  );
  const suggestedWalls = availableWalls.walls.filter(
    (wall) => !dismissedWalls.has(wall.id),
  );
  const selectedSuggestion = suggestedWalls.find(
    (wall) => wall.id === suggestionId,
  );
  const [tool, setTool] = useState<Tool>("select");
  const [suspendedTool, setSuspendedTool] = useState<Tool | null>(null);
  const [holeFor, setHoleFor] = useState<string | null>(null);
  const [selection, setSelection] = useState<RoutingSelection | null>(null);
  const [ring, setRing] = useState(0);
  const [draft, setDraft] = useState<RoutingPoint[]>([]);
  const [doorDraft, setDoorDraft] = useState<{
    barrierId: string;
    start: number;
  } | null>(null);
  const [keyboardPoint, setKeyboardPoint] = useState<RoutingPoint>([
    floor.x + floor.width / 2,
    floor.y + floor.height / 2,
  ]);
  const keyboardPointRef = useRef(keyboardPoint);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [hover, setHover] = useState<RoutingPoint | null>(null);
  const [notice, setNotice] = useState("");
  const [view, setView] = useState(() => fitRoutingView(document.geometry));
  const viewRef = useRef(view);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map<number, RoutingPoint>());
  const gesture = useRef<Gesture | null>(null);
  const sourceScale = Math.max(
    0.01,
    Math.min(stageSize.width / width, stageSize.height / height) * view.zoom,
  );
  const sceneDocument = useMemo<MapDocument>(
    () => ({
      ...document,
      geometry: {
        ...document.geometry,
        stands: document.geometry.stands.map((stand) => ({
          ...stand,
          height: 0,
        })),
      },
    }),
    [document],
  );
  const validation = useMemo(() => {
    try {
      validateMapRouting(routing, floor);
      return "";
    } catch (error) {
      return routingEditorError(error, routing);
    }
  }, [routing, floor]);
  const selectedArea =
    selection?.kind === "area"
      ? routing.walkableAreas.find((item) => item.id === selection.id)
      : undefined;
  const selectedBarrier =
    selection?.kind === "barrier"
      ? routing.barriers.find((item) => item.id === selection.id)
      : undefined;
  const selectedDoor =
    selection?.kind === "door"
      ? routing.doors.find((item) => item.id === selection.id)
      : undefined;
  const selected = selectedArea ?? selectedBarrier ?? selectedDoor;
  const vertices =
    selectedArea?.polygon[ring] ??
    (selectedBarrier ? [selectedBarrier.start, selectedBarrier.end] : []);
  const selectedDoorBarrier = selectedDoor
    ? routing.barriers.find((barrier) => barrier.id === selectedDoor.barrierId)
    : undefined;
  const handleVertices =
    selectedDoor && selectedDoorBarrier
      ? [
          barrierPoint(selectedDoorBarrier, selectedDoor.start),
          barrierPoint(selectedDoorBarrier, selectedDoor.end),
        ]
      : vertices;
  const isDrawing = draft.length > 0 || doorDraft !== null || holeFor !== null;
  const drawingTool = tool === "pan" ? suspendedTool : tool;
  const updateRouting = (next: MapRouting) => {
    routingRef.current = history.update(next);
  };
  const updateView = (next: RoutingView) => {
    const bounded = constrainRoutingView(next, width, height);
    viewRef.current = bounded;
    setView(bounded);
  };
  const select = (next: RoutingSelection | null) => {
    setSelection(next);
    setRing(0);
    setNotice("");
  };
  const cancelDrawing = () => {
    setDraft([]);
    setDoorDraft(null);
    setHoleFor(null);
    setSuspendedTool(null);
    setHover(null);
    setSnapPoint(null);
    setNotice("");
  };
  const changeTool = (next: Tool) => {
    if (isDrawing && next === tool) return;
    if (isDrawing && next === "pan") {
      setSuspendedTool(tool);
      setTool("pan");
      setHover(null);
      return;
    }
    if (isDrawing && next === suspendedTool) {
      setTool(next);
      setSuspendedTool(null);
      return;
    }
    cancelDrawing();
    setReviewingWalls(false);
    setSuggestionId(null);
    setTool(next);
  };
  const undoRouting = (redo = false) => {
    routingRef.current = redo ? history.redo() : history.undo();
    if (selection) {
      const collection =
        selection.kind === "area"
          ? routingRef.current.walkableAreas
          : selection.kind === "barrier"
            ? routingRef.current.barriers
            : routingRef.current.doors;
      if (!collection.some((item) => item.id === selection.id)) select(null);
      else if (selection.kind === "area") {
        const area = routingRef.current.walkableAreas.find(
          (item) => item.id === selection.id,
        )!;
        setRing(Math.min(ring, area.polygon.length - 1));
      }
    }
    setSuggestionId(null);
    setNotice("");
    setSnapPoint(null);
  };
  const beginWallReview = () => {
    cancelDrawing();
    setTool("select");
    select(null);
    setSuggestionId(null);
    setReviewingWalls(true);
  };
  const acceptSuggestedWall = () => {
    if (!selectedSuggestion || routingRef.current.barriers.length >= 256)
      return;
    const barrier = {
      id: newId("barrier"),
      name: `Imported wall ${routingRef.current.barriers.length + 1}`,
      start: selectedSuggestion.start,
      end: selectedSuggestion.end,
    };
    const next = {
      ...routingRef.current,
      barriers: [...routingRef.current.barriers, barrier],
    };
    try {
      validateMapRouting(next, floor);
    } catch (error) {
      setNotice(routingEditorError(error, next));
      return;
    }
    updateRouting(next);
    setSuggestionId(null);
    setNotice(
      `${barrier.name} added. Review the next wall, or choose Done reviewing.`,
    );
  };
  const snapToMap = (
    point: RoutingPoint,
    bypass = false,
    vertex?: number,
    snapRouting = routingRef.current,
  ) => {
    if (
      !snapping ||
      bypass ||
      tool === "door" ||
      (vertex !== undefined && selection?.kind === "door")
    ) {
      setSnapPoint(null);
      return point;
    }
    const result = snapRoutingPoint(
      point,
      snapRouting,
      sourceWalls.walls,
      9 / sourceScale,
      selection && vertex !== undefined
        ? { selection, ring, vertex }
        : undefined,
    );
    setSnapPoint(result.snapped ? result.point : null);
    return result.point;
  };
  const requestClose = () => {
    if (!closing.current) {
      closing.current = true;
      closeRef.current();
    }
  };
  const outerPoint = (client: RoutingPoint): RoutingPoint | null => {
    const matrix = svgRef.current?.getScreenCTM();
    if (!matrix) return null;
    const point = new DOMPoint(...client).matrixTransform(matrix.inverse());
    return [point.x, point.y];
  };
  const sourcePoint = (client: RoutingPoint): RoutingPoint | null => {
    const outer = outerPoint(client);
    return outer
      ? routingSourcePoint(outer, viewRef.current, width, height)
      : null;
  };
  const zoomTo = (zoom: number, client?: RoutingPoint) => {
    const current = viewRef.current;
    const nextZoom = Math.max(1, Math.min(12, zoom));
    const outer = client ? outerPoint(client) : null;
    const anchor = client ? sourcePoint(client) : null;
    updateView({
      zoom: nextZoom,
      centerX:
        outer && anchor
          ? anchor[0] - (outer[0] - width / 2) / nextZoom
          : current.centerX,
      centerY:
        outer && anchor
          ? anchor[1] - (outer[1] - height / 2) / nextZoom
          : current.centerY,
    });
  };
  const addArea = () => {
    if (draft.length < 3) return;
    const containingArea = holeFor
      ? routingRef.current.walkableAreas.find((area) => area.id === holeFor)
      : undefined;
    if (holeFor && !containingArea) {
      setNotice("Select a walkable area before adding an exclusion.");
      return;
    }
    const area = containingArea
      ? { ...containingArea, polygon: [...containingArea.polygon, draft] }
      : {
          id: newId("area"),
          name: `Walkable area ${routingRef.current.walkableAreas.length + 1}`,
          polygon: [draft],
        };
    const next = {
      ...routingRef.current,
      walkableAreas: containingArea
        ? routingRef.current.walkableAreas.map((item) =>
            item.id === containingArea.id ? area : item,
          )
        : [...routingRef.current.walkableAreas, area],
    };
    try {
      validateMapRouting(next, floor);
    } catch (error) {
      setNotice(routingEditorError(error, next));
      return;
    }
    updateRouting(next);
    select({ kind: "area", id: area.id });
    if (containingArea) setRing(area.polygon.length - 1);
    changeTool("select");
  };
  const addPoint = (point: RoutingPoint) => {
    const inside = clampRoutingPoint(point, floor);
    if (Math.hypot(point[0] - inside[0], point[1] - inside[1]) > 0.01) {
      setNotice("Place the point inside the floor boundary.");
      return;
    }
    setNotice("");
    if (tool === "area") {
      if (draft.length >= 128) {
        setNotice(
          "An area can have up to 128 points. Finish this area before starting another.",
        );
        return;
      }
      if (
        draft.length &&
        Math.hypot(
          point[0] - draft[draft.length - 1][0],
          point[1] - draft[draft.length - 1][1],
        ) < 0.01
      )
        return;
      setDraft([...draft, inside]);
    } else if (tool === "barrier") {
      if (!draft.length) {
        setDraft([inside]);
        return;
      }
      if (Math.hypot(point[0] - draft[0][0], point[1] - draft[0][1]) < 0.01) {
        setNotice("Choose a different point for the other end of the barrier.");
        return;
      }
      const barrier = {
        id: newId("barrier"),
        name: `Barrier ${routingRef.current.barriers.length + 1}`,
        start: draft[0],
        end: inside,
      };
      updateRouting({
        ...routingRef.current,
        barriers: [...routingRef.current.barriers, barrier],
      });
      select({ kind: "barrier", id: barrier.id });
      changeTool("select");
    } else if (tool === "door") {
      const candidates = routingRef.current.barriers
        .filter((item) => !doorDraft || item.id === doorDraft.barrierId)
        .map((barrier) => ({ barrier, ...projectOntoBarrier(point, barrier) }))
        .sort((a, b) => a.distance - b.distance);
      const nearest = candidates[0];
      if (!nearest || nearest.distance * sourceScale > 24) {
        setNotice(
          doorDraft
            ? "Choose the other doorway edge on the same barrier."
            : "Click a barrier to place the first edge of the doorway.",
        );
        return;
      }
      if (!doorDraft) {
        setDoorDraft({
          barrierId: nearest.barrier.id,
          start: nearest.fraction,
        });
        return;
      }
      const start = Math.min(doorDraft.start, nearest.fraction);
      const end = Math.max(doorDraft.start, nearest.fraction);
      if (end - start < 0.001) {
        setNotice(
          "Choose a different point for the other edge of the doorway.",
        );
        return;
      }
      const door = {
        id: newId("door"),
        name: `Doorway ${routingRef.current.doors.length + 1}`,
        barrierId: nearest.barrier.id,
        start,
        end,
      };
      const next = {
        ...routingRef.current,
        doors: [...routingRef.current.doors, door],
      };
      try {
        validateMapRouting(next, floor);
      } catch (error) {
        setNotice(routingEditorError(error, next));
        return;
      }
      updateRouting(next);
      select({ kind: "door", id: door.id });
      changeTool("select");
    }
  };

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const page = globalThis.document;
    const opener = returnFocusTo ?? page.activeElement;
    const overflow = page.body.style.overflow;
    page.body.style.overflow = "hidden";
    dialog.showModal();
    dialog.querySelector<HTMLButtonElement>(".em-routing-tool")?.focus();
    return () => {
      closing.current = true;
      dialog.close();
      page.body.style.overflow = overflow;
      queueMicrotask(() => {
        if (opener instanceof HTMLElement && opener.isConnected)
          opener.focus({ preventScroll: true });
      });
    };
  }, []);
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const bounds = stage.getBoundingClientRect();
      setStageSize({ width: bounds.width, height: bounds.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const beginPinch = () => {
    if (gesture.current?.kind === "vertex") routingRef.current = history.end();
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return;
    const anchor = sourcePoint(midpoint(a, b));
    if (anchor)
      gesture.current = {
        kind: "pinch",
        distance: Math.max(1, Math.hypot(a[0] - b[0], a[1] - b[1])),
        zoom: viewRef.current.zoom,
        anchor,
      };
  };
  const pointerDown = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const client: RoutingPoint = [event.clientX, event.clientY];
    pointers.current.set(event.pointerId, client);
    stageRef.current?.setPointerCapture(event.pointerId);
    if (pointers.current.size > 1) {
      beginPinch();
      return;
    }
    const outer = outerPoint(client);
    const source = sourcePoint(client);
    if (!outer || !source) return;
    const target = event.target instanceof Element ? event.target : null;
    // Lock the action at pointer-down so releasing Shift mid-drag cannot
    // turn a camera gesture into a geometry edit.
    const pan = tool === "pan" || (tool === "select" && event.shiftKey);
    const selectOnTap = tool === "pan" && event.shiftKey && !isDrawing;
    const suggestion =
      reviewingWalls && !pan
        ? target?.closest<SVGElement>("[data-routing-suggestion]")
        : null;
    if (suggestion) {
      select(null);
      setSuggestionId(suggestion.dataset.routingSuggestion!);
      gesture.current = null;
      return;
    }
    const shape = target?.closest<SVGElement>("[data-routing-kind]");
    const targetSelection: RoutingSelection | null = shape
      ? {
          kind: shape.dataset.routingKind as RoutingSelection["kind"],
          id: shape.dataset.routingId!,
        }
      : null;
    const vertex =
      tool === "select" && !pan
        ? target?.closest<SVGElement>("[data-routing-vertex]")
        : null;
    const index = vertex ? Number(vertex.dataset.routingVertex) : undefined;
    if (vertex && index !== undefined)
      stageRef.current
        ?.querySelector<SVGElement>(
          `.em-routing-vertex[data-routing-vertex="${index}"]`,
        )
        ?.focus({ preventScroll: true });
    if (vertex) history.begin();
    gesture.current = {
      kind: vertex ? "vertex" : pan ? "pan" : "tap",
      pointerId: event.pointerId,
      startClient: client,
      startOuter: outer,
      startSource: source,
      startView: viewRef.current,
      startRouting: vertex ? routingRef.current : undefined,
      startVertex:
        index === undefined
          ? undefined
          : (handleVertices[index] as RoutingPoint),
      vertex: index,
      tapSelection: selectOnTap ? targetSelection : undefined,
      moved: false,
    };
    if (tool === "select" && !pan && !vertex) {
      setSuggestionId(null);
      select(targetSelection);
    }
  };
  const pointerMove = (event: PointerEvent) => {
    const client: RoutingPoint = [event.clientX, event.clientY];
    if (!pointers.current.has(event.pointerId)) {
      if (tool === "area" || tool === "barrier" || tool === "door") {
        const point = sourcePoint(client);
        setHover(point ? snapToMap(point, event.altKey) : null);
      }
      return;
    }
    pointers.current.set(event.pointerId, client);
    const active = gesture.current;
    if (!active) return;
    if (active.kind === "pinch") {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const outer = outerPoint(midpoint(a, b));
      if (!outer) return;
      const zoom = Math.max(
        1,
        Math.min(
          12,
          (active.zoom * Math.hypot(a[0] - b[0], a[1] - b[1])) /
            active.distance,
        ),
      );
      updateView({
        zoom,
        centerX: active.anchor[0] - (outer[0] - width / 2) / zoom,
        centerY: active.anchor[1] - (outer[1] - height / 2) / zoom,
      });
      return;
    }
    if (active.pointerId !== event.pointerId) return;
    if (
      Math.hypot(
        client[0] - active.startClient[0],
        client[1] - active.startClient[1],
      ) > 4
    ) {
      active.moved = true;
      setDragging(true);
    }
    if (!active.moved) return;
    if (active.kind === "pan") {
      const outer = outerPoint(client);
      if (outer)
        updateView({
          ...active.startView,
          centerX:
            active.startView.centerX -
            (outer[0] - active.startOuter[0]) / active.startView.zoom,
          centerY:
            active.startView.centerY -
            (outer[1] - active.startOuter[1]) / active.startView.zoom,
        });
    } else if (
      active.kind === "vertex" &&
      selection &&
      active.vertex !== undefined &&
      active.startVertex
    ) {
      const source = sourcePoint(client);
      if (source)
        updateRouting(
          moveRoutingVertex(
            routingRef.current,
            selection,
            ring,
            active.vertex,
            clampRoutingPoint(
              snapToMap(
                [
                  active.startVertex[0] + source[0] - active.startSource[0],
                  active.startVertex[1] + source[1] - active.startSource[1],
                ],
                event.altKey,
                active.vertex,
                active.startRouting,
              ),
              floor,
            ),
          ),
        );
    }
  };
  const finishPointer = (event: PointerEvent, cancelled = false) => {
    if (!pointers.current.has(event.pointerId)) return;
    if (!cancelled) pointerMove(event);
    const active = gesture.current;
    if (
      !cancelled &&
      active?.kind === "pan" &&
      !active.moved &&
      active.tapSelection !== undefined
    ) {
      select(active.tapSelection);
    }
    if (
      !cancelled &&
      active?.kind === "tap" &&
      !active.moved &&
      tool !== "select"
    ) {
      const point = sourcePoint([event.clientX, event.clientY]);
      if (point) addPoint(snapToMap(point, event.altKey));
    }
    if (active?.kind === "vertex") routingRef.current = history.end();
    setSnapPoint(null);
    pointers.current.delete(event.pointerId);
    if (stageRef.current?.hasPointerCapture(event.pointerId))
      stageRef.current.releasePointerCapture(event.pointerId);
    if (pointers.current.size > 1) beginPinch();
    else if (pointers.current.size === 1) {
      const [pointerId, client] = [...pointers.current.entries()][0];
      const outer = outerPoint(client);
      const source = sourcePoint(client);
      if (outer && source)
        gesture.current = {
          kind: "pan",
          pointerId,
          startClient: client,
          startOuter: outer,
          startSource: source,
          startView: viewRef.current,
          moved: true,
        };
    } else {
      gesture.current = null;
      setDragging(false);
    }
  };
  const editVertex = (index: number, axis: 0 | 1, value: number) => {
    if (!selection || !Number.isFinite(value)) return;
    const point = [...vertices[index]] as RoutingPoint;
    point[axis] = (value / 100) * (axis === 0 ? width : height);
    updateRouting(
      moveRoutingVertex(
        routingRef.current,
        selection,
        ring,
        index,
        clampRoutingPoint(point, floor),
      ),
    );
  };
  const vertexKeyDown = (event: KeyboardEvent, index: number) => {
    const changes: Record<string, RoutingPoint> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const delta = changes[event.key];
    if (!delta || !selection) return;
    const currentPoint = routingSelectionVertices(
      routingRef.current,
      selection,
      ring,
    )[index];
    if (!currentPoint) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 0.01 : 0.001;
    updateRouting(
      moveRoutingVertex(
        routingRef.current,
        selection,
        ring,
        index,
        clampRoutingPoint(
          [
            currentPoint[0] + delta[0] * width * step,
            currentPoint[1] + delta[1] * height * step,
          ],
          floor,
        ),
      ),
    );
  };
  const rename = (name: string) => {
    if (!selection) return;
    if (selection.kind === "area")
      updateRouting({
        ...routing,
        walkableAreas: routing.walkableAreas.map((item) =>
          item.id === selection.id ? { ...item, name } : item,
        ),
      });
    else if (selection.kind === "barrier")
      updateRouting({
        ...routing,
        barriers: routing.barriers.map((item) =>
          item.id === selection.id ? { ...item, name } : item,
        ),
      });
    else
      updateRouting({
        ...routing,
        doors: routing.doors.map((item) =>
          item.id === selection.id ? { ...item, name } : item,
        ),
      });
  };
  const updateDoor = (values: Partial<NonNullable<typeof selectedDoor>>) => {
    if (selectedDoor)
      updateRouting({
        ...routing,
        doors: routing.doors.map((door) =>
          door.id === selectedDoor.id ? { ...door, ...values } : door,
        ),
      });
  };
  const hint = reviewingWalls
    ? "Choose a dashed amber line on the map, then Add wall. Check that it is a real wall and keep doorway gaps open. Shift + drag pans the map."
    : tool === "select"
      ? "Choose a shape or a list item. Drag its points to adjust; arrow keys move focused points. Shift + drag pans the map."
      : tool === "pan"
        ? `${suspendedTool ? "Drawing paused. " : ""}Drag the map to pan. Pinch, scroll or use the zoom buttons to see more detail.${suspendedTool ? " Resume drawing when you’re ready." : " Shift + click selects a shape."}`
        : tool === "area"
          ? holeFor
            ? "Click around an excluded area inside the selected walkable boundary, then finish the exclusion. It will be excluded from this walkable area."
            : "Click around the walkable boundary, then finish the area. Outside these areas, routes are blocked."
          : tool === "barrier"
            ? "Click the two ends of a wall or barrier. Routes cannot cross it unless there is an open doorway."
            : doorDraft
              ? "Click the other edge of the doorway on the same barrier. Open doorways allow routes through."
              : "Click a barrier to mark the first edge of a doorway, then click its other edge.";
  const doorPreviewBarrier = doorDraft
    ? routing.barriers.find((item) => item.id === doorDraft.barrierId)
    : null;
  const doorPreview =
    doorPreviewBarrier && doorDraft
      ? [
          barrierPoint(doorPreviewBarrier, doorDraft.start),
          hover
            ? projectOntoBarrier(hover, doorPreviewBarrier).point
            : barrierPoint(doorPreviewBarrier, doorDraft.start),
        ]
      : [];

  return createPortal(
    <dialog
      ref={dialogRef}
      className="em-routing-dialog"
      aria-label="Edit routing and access"
      aria-describedby={hintId}
      onCancel={(event) => {
        event.preventDefault();
        if (isDrawing) cancelDrawing();
        else requestClose();
      }}
      onClose={requestClose}
      onKeyDown={(event) => {
        const key = event.key.toLowerCase();
        const target =
          event.target instanceof HTMLElement ? event.target : null;
        if (
          (event.metaKey || event.ctrlKey) &&
          !event.altKey &&
          (key === "z" || key === "y") &&
          !target?.closest("input, textarea, [contenteditable=true]")
        ) {
          event.preventDefault();
          event.stopPropagation();
          if (!isDrawing) undoRouting(event.shiftKey || key === "y");
          else if (key === "z" && !event.shiftKey) {
            if (draft.length) setDraft(draft.slice(0, -1));
            else if (doorDraft) setDoorDraft(null);
          }
          return;
        }
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "s"
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <div className="em-routing-content">
        <header className="em-routing-header">
          <div>
            <h2>Routing &amp; access</h2>
            <p>
              Define where visitors can walk. Changes stay in the draft until
              published.
            </p>
          </div>
          <button
            type="button"
            className="em-icon-button"
            aria-label="Close routing editor"
            onClick={requestClose}
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>
        <div className="em-routing-start">
          <div>
            <strong>
              {routing.barriers.length || routing.walkableAreas.length
                ? "Refine the walking routes"
                : "Start with the building plan"}
            </strong>
            <p>
              The floor is already walkable and booths are avoided. Add walls
              where people cannot pass, then mark any door openings.
            </p>
          </div>
          {sourceWalls.walls.length > 0 && (
            <button
              type="button"
              className="em-button em-button-secondary"
              disabled={isDrawing || reviewingWalls || !suggestedWalls.length}
              onClick={beginWallReview}
            >
              <ScanLine size={16} aria-hidden="true" /> Review imported walls
            </button>
          )}
          {!sourceWalls.walls.length && !routing.barriers.length && (
            <button
              type="button"
              className="em-button em-button-secondary"
              disabled={isDrawing}
              onClick={() => changeTool("barrier")}
            >
              Draw a wall
            </button>
          )}
        </div>
        <div
          className="em-routing-tools"
          role="group"
          aria-label="Routing tools"
        >
          {tools.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className="em-routing-tool"
              aria-pressed={tool === id}
              disabled={
                (id === "door" && routing.barriers.length === 0) ||
                (isDrawing && id !== "pan" && id !== drawingTool)
              }
              onClick={() => changeTool(id)}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
          <div
            className="em-routing-history"
            role="group"
            aria-label="Routing edit history"
          >
            <button
              type="button"
              className="em-icon-button"
              aria-label="Undo routing edit"
              title="Undo (⌘Z / Ctrl+Z)"
              disabled={isDrawing || !history.canUndo}
              onClick={() => undoRouting()}
            >
              <Undo2 size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="em-icon-button"
              aria-label="Redo routing edit"
              title="Redo (⌘⇧Z / Ctrl+Shift+Z)"
              disabled={isDrawing || !history.canRedo}
              onClick={() => undoRouting(true)}
            >
              <Redo2 size={17} aria-hidden="true" />
            </button>
          </div>
        </div>
        <p id={hintId} className="em-routing-hint">
          {hint}
        </p>
        <div className="em-routing-layout">
          <div className="em-routing-canvas-column">
            <div className="em-routing-camera">
              <label
                className="em-routing-snap-control"
                title="Snap points to nearby walls and corners. Hold Alt to place freely."
              >
                <input
                  type="checkbox"
                  checked={snapping}
                  onChange={(event) => {
                    setSnapping(event.currentTarget.checked);
                    setSnapPoint(null);
                  }}
                />
                Snap to walls
              </label>
              <div>
                <button
                  type="button"
                  className="em-icon-button"
                  aria-label="Zoom out"
                  disabled={view.zoom <= 1}
                  onClick={() => zoomTo(viewRef.current.zoom / 1.5)}
                >
                  <Minus size={17} aria-hidden="true" />
                </button>
                <output>{Math.round(view.zoom * 100)}%</output>
                <button
                  type="button"
                  className="em-icon-button"
                  aria-label="Zoom in"
                  disabled={view.zoom >= 12}
                  onClick={() => zoomTo(viewRef.current.zoom * 1.5)}
                >
                  <Plus size={17} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="em-button em-button-secondary"
                  onClick={() => updateView(fitRoutingView(document.geometry))}
                >
                  <Maximize size={15} aria-hidden="true" />
                  Fit map
                </button>
              </div>
            </div>
            <div
              ref={stageRef}
              className="em-routing-map"
              data-tool={tool}
              data-dragging={dragging || undefined}
              data-panning={
                (dragging && gesture.current?.kind === "pan") || undefined
              }
              data-testid="routing-map"
              onPointerDown={pointerDown}
              onPointerMove={pointerMove}
              onPointerUp={(event) => finishPointer(event)}
              onPointerCancel={(event) => finishPointer(event, true)}
              onLostPointerCapture={(event) => finishPointer(event, true)}
              onPointerLeave={() => {
                if (!gesture.current) {
                  setHover(null);
                  setSnapPoint(null);
                }
              }}
              onWheel={(event) => {
                event.preventDefault();
                if (gesture.current) return;
                const delta =
                  event.deltaY *
                  (event.deltaMode === 1
                    ? 16
                    : event.deltaMode === 2
                      ? 300
                      : 1);
                zoomTo(viewRef.current.zoom * Math.exp(-delta * 0.002), [
                  event.clientX,
                  event.clientY,
                ]);
              }}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
                role="group"
                aria-label="Routing drawing surface"
                tabindex={0}
                onFocus={(event) => {
                  if (event.target === event.currentTarget) {
                    setKeyboardFocused(true);
                    const point = clampRoutingPoint(
                      [viewRef.current.centerX, viewRef.current.centerY],
                      floor,
                    );
                    keyboardPointRef.current = point;
                    setKeyboardPoint(point);
                  }
                }}
                onBlur={() => setKeyboardFocused(false)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  const moves: Record<string, RoutingPoint> = {
                    ArrowLeft: [-1, 0],
                    ArrowRight: [1, 0],
                    ArrowUp: [0, -1],
                    ArrowDown: [0, 1],
                  };
                  const delta = moves[event.key];
                  if (delta) {
                    event.preventDefault();
                    event.stopPropagation();
                    const step = event.shiftKey ? 0.02 : 0.005;
                    if (tool === "pan")
                      updateView({
                        ...viewRef.current,
                        centerX:
                          viewRef.current.centerX +
                          (delta[0] * width * step) / viewRef.current.zoom,
                        centerY:
                          viewRef.current.centerY +
                          (delta[1] * height * step) / viewRef.current.zoom,
                      });
                    else {
                      const next = clampRoutingPoint(
                        [
                          keyboardPointRef.current[0] +
                            (delta[0] * width * step) / viewRef.current.zoom,
                          keyboardPointRef.current[1] +
                            (delta[1] * height * step) / viewRef.current.zoom,
                        ],
                        floor,
                      );
                      keyboardPointRef.current = next;
                      setKeyboardPoint(next);
                      setHover(next);
                    }
                  } else if (
                    (event.key === "Enter" || event.key === " ") &&
                    tool !== "select" &&
                    tool !== "pan"
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    addPoint(keyboardPointRef.current);
                  }
                }}
              >
                <g
                  transform={`translate(${width / 2} ${height / 2}) scale(${view.zoom}) translate(${-view.centerX} ${-view.centerY})`}
                >
                  <g pointerEvents="none" aria-hidden="true">
                    <RoutingMapScene document={sceneDocument} logos={logos} />
                  </g>
                  <g pointerEvents="none" aria-hidden="true">
                    {document.geometry.stands
                      .filter((stand) => stand.hidden)
                      .map((stand) => (
                        <g key={stand.id}>
                          {stand.polygons.map((polygon, index) => (
                            <polygon
                              key={index}
                              points={pointList(polygon)}
                              className="em-routing-hidden-booth"
                              vectorEffect="non-scaling-stroke"
                            >
                              <title>
                                {stand.id}: hidden booth, routing obstacle
                              </title>
                            </polygon>
                          ))}
                        </g>
                      ))}
                  </g>
                  {previewRoute && (
                    <g
                      className="em-routing-test-route"
                      data-testid="routing-test-route"
                      pointerEvents="none"
                      aria-hidden="true"
                    >
                      <path
                        d={previewRoute.path}
                        className="em-routing-test-route-halo"
                        vectorEffect="non-scaling-stroke"
                      />
                      <path
                        d={previewRoute.path}
                        vectorEffect="non-scaling-stroke"
                      />
                      {[
                        previewRoute.points[0],
                        previewRoute.points[previewRoute.points.length - 1],
                      ]
                        .filter(Boolean)
                        .map((point, index) => (
                          <circle
                            key={index}
                            cx={point[0]}
                            cy={point[1]}
                            r={5 / sourceScale}
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                    </g>
                  )}
                  <g className="em-routing-shapes">
                    {routing.walkableAreas.map((area) => (
                      <path
                        key={area.id}
                        d={areaPath(area.polygon)}
                        fillRule="evenodd"
                        className="em-routing-area"
                        data-selected={
                          (selection?.kind === "area" &&
                            selection.id === area.id) ||
                          undefined
                        }
                        data-routing-kind="area"
                        data-routing-id={area.id}
                        vectorEffect="non-scaling-stroke"
                      >
                        <title>{area.name}</title>
                      </path>
                    ))}
                    {routing.barriers.map((barrier) => (
                      <g
                        key={barrier.id}
                        data-routing-kind="barrier"
                        data-routing-id={barrier.id}
                      >
                        <line
                          x1={barrier.start[0]}
                          y1={barrier.start[1]}
                          x2={barrier.end[0]}
                          y2={barrier.end[1]}
                          className="em-routing-hit-line"
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1={barrier.start[0]}
                          y1={barrier.start[1]}
                          x2={barrier.end[0]}
                          y2={barrier.end[1]}
                          className="em-routing-barrier"
                          data-selected={
                            selection?.id === barrier.id || undefined
                          }
                          vectorEffect="non-scaling-stroke"
                          pointerEvents="none"
                        >
                          <title>{barrier.name}</title>
                        </line>
                      </g>
                    ))}
                    {routing.doors.map((door) => {
                      const barrier = routing.barriers.find(
                        (item) => item.id === door.barrierId,
                      );
                      if (!barrier) return null;
                      const a = barrierPoint(barrier, door.start);
                      const b = barrierPoint(barrier, door.end);
                      return (
                        <g
                          key={door.id}
                          data-routing-kind="door"
                          data-routing-id={door.id}
                        >
                          <line
                            x1={a[0]}
                            y1={a[1]}
                            x2={b[0]}
                            y2={b[1]}
                            className="em-routing-hit-line"
                            vectorEffect="non-scaling-stroke"
                          />
                          <line
                            x1={a[0]}
                            y1={a[1]}
                            x2={b[0]}
                            y2={b[1]}
                            className="em-routing-door-halo"
                            vectorEffect="non-scaling-stroke"
                            pointerEvents="none"
                          />
                          <line
                            x1={a[0]}
                            y1={a[1]}
                            x2={b[0]}
                            y2={b[1]}
                            className="em-routing-door"
                            data-closed={door.closed || undefined}
                            data-selected={
                              selection?.id === door.id || undefined
                            }
                            vectorEffect="non-scaling-stroke"
                            pointerEvents="none"
                          >
                            <title>
                              {door.name}
                              {door.closed ? " (closed)" : " (open)"}
                            </title>
                          </line>
                        </g>
                      );
                    })}
                    {draft.length > 0 && (
                      <g pointerEvents="none">
                        <polyline
                          points={pointList(
                            hover
                              ? [...draft, clampRoutingPoint(hover, floor)]
                              : draft,
                          )}
                          className="em-routing-draft"
                          vectorEffect="non-scaling-stroke"
                        />
                        {draft.map((point, index) => (
                          <circle
                            key={index}
                            cx={point[0]}
                            cy={point[1]}
                            r={4 / sourceScale}
                            className="em-routing-draft-point"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </g>
                    )}
                    {doorPreview.length === 2 && (
                      <polyline
                        points={pointList(doorPreview)}
                        className="em-routing-door-preview"
                        vectorEffect="non-scaling-stroke"
                        pointerEvents="none"
                      />
                    )}
                    {tool === "select" &&
                      handleVertices.map((point, index) => (
                        <g key={index}>
                          <circle
                            cx={point[0]}
                            cy={point[1]}
                            r={18 / sourceScale}
                            fill="transparent"
                            data-routing-vertex={index}
                          />
                          <circle
                            cx={point[0]}
                            cy={point[1]}
                            r={5 / sourceScale}
                            className="em-routing-vertex"
                            data-routing-vertex={index}
                            vectorEffect="non-scaling-stroke"
                            tabindex={0}
                            role="button"
                            aria-label={`Move point ${index + 1} of ${selected?.name || "selected shape"}`}
                            onKeyDown={(event) => vertexKeyDown(event, index)}
                          />
                        </g>
                      ))}
                    {snapPoint && (
                      <circle
                        cx={snapPoint[0]}
                        cy={snapPoint[1]}
                        r={7 / sourceScale}
                        className="em-routing-snap-point"
                        vectorEffect="non-scaling-stroke"
                        pointerEvents="none"
                        aria-hidden="true"
                      />
                    )}
                    {keyboardFocused && tool !== "select" && tool !== "pan" && (
                      <g
                        className="em-routing-keyboard-cursor"
                        pointerEvents="none"
                        aria-hidden="true"
                      >
                        <circle
                          cx={keyboardPoint[0]}
                          cy={keyboardPoint[1]}
                          r={10 / sourceScale}
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1={keyboardPoint[0] - 15 / sourceScale}
                          y1={keyboardPoint[1]}
                          x2={keyboardPoint[0] + 15 / sourceScale}
                          y2={keyboardPoint[1]}
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1={keyboardPoint[0]}
                          y1={keyboardPoint[1] - 15 / sourceScale}
                          x2={keyboardPoint[0]}
                          y2={keyboardPoint[1] + 15 / sourceScale}
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    )}
                  </g>
                  {reviewingWalls && (
                    <g className="em-routing-suggestions">
                      {suggestedWalls.map((wall, index) => (
                        <g key={wall.id} data-routing-suggestion={wall.id}>
                          <line
                            x1={wall.start[0]}
                            y1={wall.start[1]}
                            x2={wall.end[0]}
                            y2={wall.end[1]}
                            className="em-routing-hit-line"
                            vectorEffect="non-scaling-stroke"
                          />
                          <line
                            x1={wall.start[0]}
                            y1={wall.start[1]}
                            x2={wall.end[0]}
                            y2={wall.end[1]}
                            className="em-routing-suggested-wall"
                            data-selected={
                              wall.id === suggestionId || undefined
                            }
                            vectorEffect="non-scaling-stroke"
                            pointerEvents="none"
                          >
                            <title>Suggested wall {index + 1}</title>
                          </line>
                        </g>
                      ))}
                    </g>
                  )}
                </g>
              </svg>
            </div>
            {tool !== "select" && (
              <p className="em-routing-note">
                Keyboard: focus the map, use arrow keys to{" "}
                {tool === "pan"
                  ? "pan"
                  : "position the crosshair, then Enter to add a point"}
                . Hold Shift for larger steps.
              </p>
            )}
            {isDrawing && (
              <div className="em-routing-drawing-actions">
                <span>
                  {drawingTool === "door"
                    ? "First doorway edge placed"
                    : `${draft.length} ${draft.length === 1 ? "point" : "points"} placed`}
                </span>
                {drawingTool === "area" && (
                  <button
                    type="button"
                    className="em-button"
                    disabled={draft.length < 3}
                    onClick={addArea}
                  >
                    <Check size={15} aria-hidden="true" />
                    {holeFor ? "Finish exclusion" : "Finish area"}
                  </button>
                )}
                {suspendedTool && (
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    onClick={() => changeTool(suspendedTool)}
                  >
                    Resume drawing
                  </button>
                )}
                {draft.length > 0 && (
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    onClick={() => setDraft(draft.slice(0, -1))}
                  >
                    <Undo2 size={15} aria-hidden="true" />
                    Undo point
                  </button>
                )}
                <button
                  type="button"
                  className="em-button em-button-secondary"
                  onClick={cancelDrawing}
                >
                  Cancel drawing
                </button>
              </div>
            )}
            <div
              className="em-routing-legend"
              role="group"
              aria-label="Map key"
            >
              <span>
                <i className="em-routing-key-area" />
                Walkable area
              </span>
              <span>
                <i className="em-routing-key-barrier" />
                Barrier
              </span>
              <span>
                <i className="em-routing-key-door" />
                Open doorway
              </span>
              <span>
                <i className="em-routing-key-closed" />
                Closed doorway
              </span>
              {document.geometry.stands.some((stand) => stand.hidden) && (
                <span>
                  <i className="em-routing-key-hidden" />
                  Hidden booth (obstacle)
                </span>
              )}
            </div>
            <p className="em-routing-note">
              Dashed booth outlines are hidden booths; they still block routes.
              Imported building detail becomes a barrier only after you add it.
              Leave walkable areas empty to use the full floor, or draw an area
              to restrict it. Hold Alt to place points without snapping.
            </p>
          </div>
          <aside className="em-routing-sidebar" aria-label="Routing shapes">
            {reviewingWalls && (
              <section
                className="em-routing-wall-review"
                aria-label="Imported wall suggestions"
              >
                <h3>Review imported walls</h3>
                <p>
                  Suggestions follow long lines in the drawing. They may include
                  furniture or other detail; add only the walls you recognise.
                  Doorways need your review.
                </p>
                <div className="em-routing-list em-routing-suggestion-list">
                  {suggestedWalls.map((wall, index) => (
                    <button
                      key={wall.id}
                      type="button"
                      aria-pressed={suggestionId === wall.id}
                      onClick={() => {
                        select(null);
                        setSuggestionId(wall.id);
                        updateView({
                          ...viewRef.current,
                          centerX: (wall.start[0] + wall.end[0]) / 2,
                          centerY: (wall.start[1] + wall.end[1]) / 2,
                        });
                      }}
                    >
                      Suggested wall {index + 1}
                    </button>
                  ))}
                </div>
                {!suggestedWalls.length && (
                  <p>
                    No more long wall lines to review. You can draw any missing
                    walls with Wall / barrier.
                  </p>
                )}
                {(sourceWalls.truncated || availableWalls.truncated) && (
                  <p>
                    Showing a limited set of the longest lines. Trace shorter
                    walls with Wall / barrier.
                  </p>
                )}
                <div className="em-routing-review-actions">
                  <button
                    type="button"
                    className="em-button"
                    disabled={!selectedSuggestion}
                    onClick={acceptSuggestedWall}
                  >
                    Add wall
                  </button>
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    disabled={!selectedSuggestion}
                    onClick={() => {
                      if (suggestionId)
                        setDismissedWalls(
                          new Set([...dismissedWalls, suggestionId]),
                        );
                      setSuggestionId(null);
                    }}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    onClick={() => {
                      setReviewingWalls(false);
                      setSuggestionId(null);
                    }}
                  >
                    Done reviewing
                  </button>
                </div>
              </section>
            )}
            <div className="em-routing-list-heading">
              <h3>Shapes</h3>
              <span>
                {routing.walkableAreas.length +
                  routing.barriers.length +
                  routing.doors.length}
              </span>
            </div>
            <div className="em-routing-list">
              {[
                ...routing.walkableAreas.map((item) => ({
                  ...item,
                  kind: "area" as const,
                })),
                ...routing.barriers.map((item) => ({
                  ...item,
                  kind: "barrier" as const,
                })),
                ...routing.doors.map((item) => ({
                  ...item,
                  kind: "door" as const,
                })),
              ].map((item) => (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  disabled={isDrawing}
                  aria-pressed={
                    selection?.id === item.id && selection.kind === item.kind
                  }
                  onClick={() => {
                    changeTool("select");
                    select({ kind: item.kind, id: item.id });
                  }}
                >
                  <i
                    className={`em-routing-key-${item.kind === "door" && "closed" in item && item.closed ? "closed" : item.kind}`}
                  />
                  <span>
                    {item.name || typeName(item.kind)}
                    <small>{typeName(item.kind)}</small>
                  </span>
                </button>
              ))}
              {!routing.walkableAreas.length && !routing.barriers.length && (
                <p>
                  No extra restrictions yet. The floor boundary and booths
                  already guide routes. Add walls or review the imported
                  suggestions to refine them.
                </p>
              )}
            </div>
            {selection && selected && (
              <section
                className="em-routing-properties"
                aria-label="Selected shape"
                onFocusCapture={(event) => {
                  if (
                    event.target instanceof HTMLInputElement &&
                    event.target.type !== "checkbox"
                  )
                    history.begin();
                }}
                onBlurCapture={(event) => {
                  if (
                    event.target instanceof HTMLInputElement &&
                    event.target.type !== "checkbox"
                  )
                    routingRef.current = history.end();
                }}
              >
                <label>
                  Name
                  <input
                    aria-label="Shape name"
                    maxLength={160}
                    value={selected.name}
                    onInput={(event) => rename(event.currentTarget.value)}
                  />
                </label>
                {selectedArea && selectedArea.polygon.length > 1 && (
                  <label>
                    Boundary
                    <select
                      value={ring}
                      onChange={(event) =>
                        setRing(Number(event.currentTarget.value))
                      }
                    >
                      {selectedArea.polygon.map((_, index) => (
                        <option value={index} key={index}>
                          {index === 0
                            ? "Outside boundary"
                            : `Excluded area ${index}`}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {selectedArea && !isDrawing && (
                  <div className="em-routing-area-actions">
                    <button
                      type="button"
                      className="em-button em-button-secondary"
                      disabled={selectedArea.polygon.length >= 8}
                      onClick={() => {
                        cancelDrawing();
                        setHoleFor(selectedArea.id);
                        setTool("area");
                      }}
                    >
                      <Plus size={14} aria-hidden="true" />
                      Exclude an area
                    </button>
                    {ring > 0 && (
                      <button
                        type="button"
                        className="em-button em-button-secondary"
                        onClick={() => {
                          updateRouting({
                            ...routingRef.current,
                            walkableAreas: routingRef.current.walkableAreas.map(
                              (area) =>
                                area.id === selectedArea.id
                                  ? {
                                      ...area,
                                      polygon: area.polygon.filter(
                                        (_, index) => index !== ring,
                                      ),
                                    }
                                  : area,
                            ),
                          });
                          setRing(0);
                        }}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                        Remove exclusion
                      </button>
                    )}
                  </div>
                )}
                {selectedBarrier && !isDrawing && (
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    onClick={() => {
                      changeTool("door");
                      setNotice(
                        `Click the two edges of the opening on ${selectedBarrier.name}.`,
                      );
                    }}
                  >
                    <DoorOpen size={15} aria-hidden="true" />
                    Add doorway
                  </button>
                )}
                {vertices.length > 0 && (
                  <details className="em-routing-exact">
                    <summary>Exact positions</summary>
                    <div className="em-routing-vertices">
                      <p>
                        Position (%) <span>X · Y</span>
                      </p>
                      {vertices.map((point, index) => (
                        <div key={index}>
                          <span>{index + 1}</span>
                          <input
                            type="number"
                            step="0.1"
                            aria-label={`Point ${index + 1} horizontal position`}
                            min={percent(floor.x, width)}
                            max={percent(floor.x + floor.width, width)}
                            value={percent(point[0], width)}
                            onInput={(event) =>
                              editVertex(
                                index,
                                0,
                                event.currentTarget.valueAsNumber,
                              )
                            }
                          />
                          <input
                            type="number"
                            step="0.1"
                            aria-label={`Point ${index + 1} vertical position`}
                            min={percent(floor.y, height)}
                            max={percent(floor.y + floor.height, height)}
                            value={percent(point[1], height)}
                            onInput={(event) =>
                              editVertex(
                                index,
                                1,
                                event.currentTarget.valueAsNumber,
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {selectedDoor && (
                  <>
                    <label>
                      Barrier
                      <select
                        value={selectedDoor.barrierId}
                        onChange={(event) =>
                          updateDoor({ barrierId: event.currentTarget.value })
                        }
                      >
                        {routing.barriers.map((barrier) => (
                          <option key={barrier.id} value={barrier.id}>
                            {barrier.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="em-routing-door-edges">
                      <label>
                        Start (%)
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          value={Number((selectedDoor.start * 100).toFixed(2))}
                          onInput={(event) => {
                            if (
                              Number.isFinite(event.currentTarget.valueAsNumber)
                            )
                              updateDoor({
                                start: event.currentTarget.valueAsNumber / 100,
                              });
                          }}
                        />
                      </label>
                      <label>
                        End (%)
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          value={Number((selectedDoor.end * 100).toFixed(2))}
                          onInput={(event) => {
                            if (
                              Number.isFinite(event.currentTarget.valueAsNumber)
                            )
                              updateDoor({
                                end: event.currentTarget.valueAsNumber / 100,
                              });
                          }}
                        />
                      </label>
                    </div>
                    <label className="em-routing-checkbox">
                      <input
                        type="checkbox"
                        checked={!!selectedDoor.closed}
                        onChange={(event) =>
                          updateDoor({ closed: event.currentTarget.checked })
                        }
                      />
                      Doorway is closed
                    </label>
                    <p className="em-routing-note">
                      Edges are measured along the selected barrier. A closed
                      doorway blocks routes.
                    </p>
                    {selectedDoorBarrier &&
                      !selectedDoor.closed &&
                      Math.hypot(
                        selectedDoorBarrier.end[0] -
                          selectedDoorBarrier.start[0],
                        selectedDoorBarrier.end[1] -
                          selectedDoorBarrier.start[1],
                      ) *
                        (selectedDoor.end - selectedDoor.start) <
                        4 && (
                        <p className="em-routing-validation" role="status">
                          This opening is too narrow for a route. Move its edges
                          farther apart.
                        </p>
                      )}
                  </>
                )}
                {selectedBarrier && (
                  <p className="em-routing-note">
                    Deleting this barrier also removes its{" "}
                    {
                      routing.doors.filter(
                        (door) => door.barrierId === selectedBarrier.id,
                      ).length
                    }{" "}
                    doorway(s).
                  </p>
                )}
                <button
                  type="button"
                  className="em-button em-button-secondary em-routing-delete"
                  disabled={isDrawing}
                  onClick={() => {
                    updateRouting(
                      removeRoutingConstraint(routingRef.current, selection),
                    );
                    select(null);
                  }}
                >
                  <Trash2 size={15} aria-hidden="true" />
                  Delete {typeName(selection.kind).toLowerCase()}
                </button>
              </section>
            )}
            <RoutingRoutePreview
              document={document}
              routing={routing}
              onRoute={receivePreviewRoute}
              disabled={isDrawing || !!validation}
            />
          </aside>
        </div>
        {(notice || validation) && (
          <p className="em-routing-validation" role="alert">
            {notice || validation}
          </p>
        )}
        <footer className="em-routing-footer">
          <p>
            {isDrawing
              ? "Finish or cancel the drawing to apply your changes."
              : "Use changes updates this draft. Save when you’re ready."}
          </p>
          <button
            type="button"
            className="em-button em-button-secondary"
            onClick={requestClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="em-button"
            disabled={!!validation || isDrawing}
            onClick={() => {
              onApply(routingRef.current);
              requestClose();
            }}
          >
            Use changes
          </button>
        </footer>
      </div>
    </dialog>,
    globalThis.document.body,
  );
}
